import React from "react";
import { Button } from "antd";
import {
  DeleteOutlined,
  CheckCircleOutlined,
  StopOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import {
  Review,
  useReviewDelete,
  useReviewUpdate,
} from "../../../hooks/api/reviews";
import { ReviewStatus } from "../../../enums/reviews";

import styles from "./index.module.scss";
import { useSimpleModal } from "../../Common/Modal/Simple";
import { useTranslation } from "react-i18next";

export interface ReviewActionProps {
  record: Review;
  onSuccess?: () => void;
}

function ReviewActions({ record, onSuccess }: ReviewActionProps) {
  const { open, contextHolder } = useSimpleModal();
  const reviewUpdate = useReviewUpdate(record.id);
  const reviewDelete = useReviewDelete(record.id);
  const { status } = record;
  const { t } = useTranslation();

  const approve = async () => {
    await reviewUpdate.fetch({
      status: ReviewStatus.approved,
    });
    onSuccess?.();
  };

  const reject = async () => {
    await reviewUpdate.fetch({
      status: ReviewStatus.rejected,
    });
    onSuccess?.();
  };

  const remove = async () => {
    open({
      icon: <ExclamationCircleFilled />,
      title: t("pages.reviews.deleteTitle"),
      content: t("pages.reviews.deleteContent"),
      cancelText: t("common.cancel"),
      okText: t("common.delete"),
      okButtonProps: { danger: true },
      onOk: () =>
        reviewDelete.fetch().then((res) => {
          if (res) {
            onSuccess?.();
          }
        }),
    });
  };

  const actionsByStatus = {
    [ReviewStatus.pending]: [
      {
        icon: <CheckCircleOutlined />,
        className: styles.iconGreen,
        onClick: approve,
      },
      {
        icon: <StopOutlined />,
        className: styles.iconRed,
        onClick: reject,
      },
      {
        icon: <DeleteOutlined />,
        onClick: remove,
      },
    ],
    [ReviewStatus.approved]: [
      {
        icon: <StopOutlined />,
        className: styles.iconRed,
        onClick: reject,
      },
      {
        icon: <DeleteOutlined />,
        onClick: remove,
      },
    ],
    [ReviewStatus.rejected]: [
      {
        icon: <CheckCircleOutlined />,
        className: styles.iconGreen,
        onClick: approve,
      },
      {
        icon: <DeleteOutlined />,
        onClick: remove,
      },
    ],
  };

  const buttons = actionsByStatus[status] ?? [];

  return (
    <div style={{ display: "flex", gap: 4 }}>
      {buttons.map((btn, index) => (
        <Button
          key={index}
          icon={btn.icon}
          className={btn.className}
          onClick={btn.onClick}
        />
      ))}
      {contextHolder}
    </div>
  );
}

export default ReviewActions;
